/**
 * JobBriefBlock — the "important facts only" card.
 *
 * Renders arrival, estimated days, suggested crew, and risk flags from the
 * canonical getJobBriefing() resolver. Used in InstallerExecutionView and in
 * the Scope Preview sheet (pre-acceptance).
 *
 * No contractValue. No revenue. No PM opinions.
 */

import { Clock, Users, CalendarRange, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { JobBriefing } from "@/data/contractor/jobBriefing";

export function JobBriefBlock({ brief }: { brief: JobBriefing }) {
  return (
    <Card data-testid="job-brief-block">
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Stat
            icon={Clock}
            label="Arrival"
            value={brief.arrival.label}
            wide
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Stat
            icon={CalendarRange}
            label="Estimated days"
            value={
              brief.estimatedDays === 1 ? "1 day" : `${brief.estimatedDays} days`
            }
          />
          <Stat
            icon={Users}
            label="Suggested crew"
            value={`${brief.suggestedCrewSize} people`}
          />
        </div>
        {brief.riskFlags.length > 0 && (
          <div
            data-testid="job-brief-flags"
            className="pt-2 border-t border-border/60"
          >
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> What's involved
            </p>
            <div className="flex flex-wrap gap-1.5">
              {brief.riskFlags.map((f) => (
                <Badge
                  key={f.key}
                  variant="outline"
                  className="text-[10px] font-normal"
                >
                  {f.label}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  wide = false,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "col-span-3" : ""}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-0.5">
        <Icon className="h-3 w-3" /> {label}
      </p>
      <p className="text-sm font-semibold leading-tight">{value}</p>
    </div>
  );
}
